import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ACCEPT_ALL_PREFERENCES,
  ANALYTICS_FIRST_PARTY_COOKIE_PREFIXES,
  ANALYTICS_FIRST_PARTY_COOKIES,
  CLARITY_FIRST_PARTY_COOKIES,
} from './cookie-consent.config';
import {
  COOKIE_CONSENT_STORAGE_KEY,
  CookieConsentPreferences,
  CookieConsentProviderId,
  CookieConsentState,
  ConsentDecision,
  DEFAULT_UNSET_CONSENT,
  LEGACY_CONSENT_STORAGE_KEY,
} from './cookie-consent.model';

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly stateSubject: BehaviorSubject<CookieConsentState | null>;
  readonly state$: Observable<CookieConsentState | null>;

  constructor() {
    this.stateSubject = new BehaviorSubject<CookieConsentState | null>(null);
    this.state$ = this.stateSubject.asObservable();
    const initial = this.loadFromStorage();
    this.stateSubject.next(initial);
  }

  /**
   * Compatibilidad con implementación anterior.
   * Históricamente `consent.analytics` solo gobernaba Microsoft Clarity.
   */
  get status(): 'granted' | 'denied' | 'unknown' {
    const state = this.getConsent();
    if (!state) {
      return 'unknown';
    }
    if (this.isFullyAccepted(state)) {
      return 'granted';
    }
    if (this.isFullyRejected(state)) {
      return 'denied';
    }
    return 'unknown';
  }

  getConsent(): CookieConsentState | null {
    return this.stateSubject.value;
  }

  hasStoredDecision(): boolean {
    return this.stateSubject.value !== null;
  }

  hasPendingOptionalDecisions(): boolean {
    const state = this.getConsent();
    if (!state) {
      return true;
    }
    return this.hasUnsetOptionalProviders(state);
  }

  shouldPromptUser(): boolean {
    if (!environment.consentRequired) {
      return false;
    }
    return this.hasPendingOptionalDecisions();
  }

  isProviderAccepted(provider: CookieConsentProviderId): boolean {
    if (!environment.consentRequired) {
      return true;
    }
    const state = this.getConsent();
    if (!state) {
      return false;
    }
    return state[provider] === 'accepted';
  }

  isProviderRejected(provider: CookieConsentProviderId): boolean {
    const state = this.getConsent();
    if (!state) {
      return false;
    }
    return state[provider] === 'rejected';
  }

  acceptAll(): CookieConsentState {
    return this.persist({
      clarity: ACCEPT_ALL_PREFERENCES.clarity,
      googleAnalytics: ACCEPT_ALL_PREFERENCES.googleAnalytics,
      youtube: ACCEPT_ALL_PREFERENCES.youtube,
    });
  }

  rejectAll(): CookieConsentState {
    return this.persist({
      clarity: 'rejected',
      googleAnalytics: 'rejected',
      youtube: 'rejected',
    });
  }

  updatePreferences(preferences: CookieConsentPreferences): CookieConsentState {
    return this.persist(preferences);
  }

  setConsent(preferences: CookieConsentPreferences): CookieConsentState {
    return this.persist(preferences);
  }

  /** @deprecated Use acceptAll / rejectAll / updatePreferences */
  grant(): void {
    this.acceptAll();
  }

  /** @deprecated Use rejectAll / updatePreferences */
  deny(): void {
    this.rejectAll();
  }

  reset(): void {
    localStorage.removeItem(COOKIE_CONSENT_STORAGE_KEY);
    localStorage.removeItem(LEGACY_CONSENT_STORAGE_KEY);
    this.stateSubject.next(null);
  }

  clearFirstPartyOptionalCookies(): void {
    CLARITY_FIRST_PARTY_COOKIES.forEach((name) => this.deleteCookie(name));
  }

  clearGoogleAnalyticsCookies(): void {
    ANALYTICS_FIRST_PARTY_COOKIES.forEach((name) => this.deleteCookie(name));
    this.listCookieNames()
      .filter((name) =>
        ANALYTICS_FIRST_PARTY_COOKIE_PREFIXES.some((prefix) => name.startsWith(prefix))
      )
      .forEach((name) => this.deleteCookie(name));
  }

  private persist(preferences: CookieConsentPreferences): CookieConsentState {
    const next: CookieConsentState = {
      version: 1,
      clarity: preferences.clarity,
      googleAnalytics: preferences.googleAnalytics,
      youtube: preferences.youtube,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(next));
    this.syncLegacyStorage(next);
    this.stateSubject.next(next);
    return next;
  }

  private loadFromStorage(): CookieConsentState | null {
    const raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as CookieConsentState;
        if (this.isValidState(parsed)) {
          return parsed;
        }
      } catch {
        // ignore corrupt payload
      }
    }
    return this.migrateLegacyConsent();
  }

  /**
   * Evidencia histórica: `consent.analytics` se escribía desde
   * `ClarityConsentModalComponent` y solo habilitaba `ClarityLoaderService`.
   * GTM se cargaba sin consentimiento en `AppComponent`.
   * Por tanto NO se amplía legacy granted a GA ni YouTube.
   */
  private migrateLegacyConsent(): CookieConsentState | null {
    const legacy = localStorage.getItem(LEGACY_CONSENT_STORAGE_KEY);
    if (legacy === 'granted') {
      return this.persist({
        clarity: 'accepted',
        googleAnalytics: 'unset',
        youtube: 'unset',
      });
    }
    if (legacy === 'denied') {
      return this.persist({
        clarity: 'rejected',
        googleAnalytics: 'unset',
        youtube: 'unset',
      });
    }
    return null;
  }

  private isValidState(value: CookieConsentState): value is CookieConsentState {
    if (!value || value.version !== 1) {
      return false;
    }
    return (
      this.isDecision(value.clarity) &&
      this.isDecision(value.googleAnalytics) &&
      this.isDecision(value.youtube)
    );
  }

  private isDecision(value: unknown): value is ConsentDecision {
    return value === 'accepted' || value === 'rejected' || value === 'unset';
  }

  private hasUnsetOptionalProviders(state: CookieConsentState): boolean {
    return (
      state.clarity === 'unset' ||
      state.googleAnalytics === 'unset' ||
      state.youtube === 'unset'
    );
  }

  private isFullyAccepted(state: CookieConsentState): boolean {
    return (
      state.clarity === 'accepted' &&
      state.googleAnalytics === 'accepted' &&
      state.youtube === 'accepted'
    );
  }

  private isFullyRejected(state: CookieConsentState): boolean {
    return (
      state.clarity === 'rejected' &&
      state.googleAnalytics === 'rejected' &&
      state.youtube === 'rejected'
    );
  }

  private syncLegacyStorage(state: CookieConsentState): void {
    if (this.isFullyAccepted(state)) {
      localStorage.setItem(LEGACY_CONSENT_STORAGE_KEY, 'granted');
      return;
    }
    if (this.isFullyRejected(state)) {
      localStorage.setItem(LEGACY_CONSENT_STORAGE_KEY, 'denied');
      return;
    }
    localStorage.removeItem(LEGACY_CONSENT_STORAGE_KEY);
  }

  private deleteCookie(name: string): void {
    if (typeof document === 'undefined') {
      return;
    }
    const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = `${name}=; expires=${expires}; path=/`;
    const host = location.hostname;
    if (host.includes('.')) {
      document.cookie = `${name}=; expires=${expires}; path=/; domain=${host}`;
      const parts = host.split('.');
      if (parts.length > 2) {
        const parent = parts.slice(-2).join('.');
        document.cookie = `${name}=; expires=${expires}; path=/; domain=.${parent}`;
      }
    }
  }

  private listCookieNames(): string[] {
    if (typeof document === 'undefined' || !document.cookie) {
      return [];
    }
    return document.cookie
      .split(';')
      .map((entry) => entry.split('=')[0]?.trim())
      .filter((name): name is string => !!name);
  }

  getUnsetPreferences(): CookieConsentPreferences {
    return { ...DEFAULT_UNSET_CONSENT };
  }
}

/** Alias retrocompatible para imports existentes. */
export { CookieConsentService as ConsentService };
